import { useEffect, useState } from "react";
import type { CaseDetail, Outcome } from "./api";

const statuses = [
  { value: "PENDING", label: "进行中" },
  { value: "WON", label: "已成单" },
  { value: "RETURNED", label: "已退货" },
  { value: "FAILED", label: "已失败" },
] as const;

export function CaseStatusSettings({
  detail,
  busy,
  mutate,
}: {
  detail: CaseDetail;
  busy: boolean;
  mutate: (body: Record<string, unknown>) => Promise<boolean>;
}) {
  const [target, setTarget] = useState<Outcome | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setTarget(null);
    setReason("");
  }, [detail.id, detail.rowVersion]);
  const failed = detail.businessOutcome === "FAILED";
  const returned = detail.businessOutcome === "RETURNED";
  const restartable = failed || returned;
  const working = busy || saving;
  const canOverrideWorkflow = detail.isPlatformAdministrator === true;
  const canWin =
    detail.permissions.statusChange &&
    !failed &&
    !returned &&
    (canOverrideWorkflow ||
      (!detail.progressReviewRequired &&
        detail.automaticProgress === "SALES_FOLLOW_UP"));
  const canReturn =
    detail.permissions.statusChange &&
    (canOverrideWorkflow || detail.businessOutcome === "WON");
  const lastFailure = failed
    ? [...detail.history]
        .reverse()
        .find(
          (event) =>
            event.eventType === "Case.outcome.v2" &&
            (event.payload.after as { businessOutcome?: string } | undefined)
              ?.businessOutcome === "FAILED",
        )?.payload.reason
    : undefined;

  async function save(next: Outcome, explanation: string) {
    if (working) return;
    setSaving(true);
    try {
      if (
        await mutate({
          action: "outcome",
          target: next,
          reason: explanation.trim(),
        })
      ) {
        setTarget(null);
        setReason("");
      }
    } finally {
      setSaving(false);
    }
  }
  function select(next: Outcome) {
    if (next === "PENDING") {
      const label = restartable ? "重启该项目" : "恢复为进行中";
      if (
        window.confirm(
          `确认${label}？\n案件：${detail.caseNumber}\n已有进度和操作历史将保留。`,
        )
      )
        void save(next, restartable ? "手动重启项目" : "将案件恢复为进行中");
    } else {
      setTarget(next);
      setReason("");
    }
  }

  return (
    <section className="case-status-settings" aria-label="状态设置">
      <h2>状态设置</h2>
      <div className="case-status-switch" role="group" aria-label="案件状态">
        {statuses.map((status) => (
          <button
            key={status.value}
            type="button"
            aria-pressed={detail.businessOutcome === status.value}
            data-status={status.value}
            disabled={
              working ||
              detail.businessOutcome === status.value ||
              (status.value === "PENDING" && restartable
                ? !detail.permissions.statusRestart
                : status.value === "WON"
                  ? !canWin
                  : status.value === "RETURNED"
                    ? !canReturn
                    : !detail.permissions.statusChange)
            }
            title={
              status.value === "WON" && !canWin
                ? failed
                  ? "请先重启该项目"
                  : "需具有状态修改权限并完成销售跟进"
                : status.value === "RETURNED" && !canReturn
                  ? "仅已成单案件可以标记为已退货"
                  : canOverrideWorkflow && status.value !== "PENDING"
                    ? "超级管理员可直接设置案件状态"
                  : undefined
            }
            onClick={() => select(status.value)}
          >
            {status.label}
          </button>
        ))}
      </div>
      {restartable && (
        <>
          {lastFailure && (
            <p className="case-failure-reason">失败原因：{lastFailure}</p>
          )}
          <button
            type="button"
            className="case-restart"
            disabled={working || !detail.permissions.statusRestart}
            onClick={() => select("PENDING")}
          >
            重启该项目
          </button>
        </>
      )}
      {target && (
        <form
          className="case-outcome-confirm"
          onSubmit={(event) => {
            event.preventDefault();
            if (target !== "WON" && !reason.trim()) return;
            void save(target, reason);
          }}
        >
          <strong>
            确认
            {target === "FAILED"
              ? "标记失败"
              : target === "WON"
                ? "成单"
                : "退货"}
            ：{detail.caseNumber}
          </strong>
          <label>
            {target === "FAILED"
              ? "失败原因（必填）"
              : target === "WON"
                ? "说明（可选）"
                : "退货原因（必填）"}
            <textarea
              autoFocus
              required={target !== "WON"}
              maxLength={2000}
              value={reason}
              disabled={working}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <button disabled={working || (target !== "WON" && !reason.trim())}>
            确认保存
          </button>
          <button
            type="button"
            disabled={working}
            onClick={() => {
              setTarget(null);
              setReason("");
            }}
          >
            取消
          </button>
        </form>
      )}
      {detail.wonAt && (
        <small>
          成单时间：{new Date(detail.wonAt).toLocaleString("zh-CN")}
        </small>
      )}
    </section>
  );
}
